import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Code, CreditCard, KeyRound, Lock, User, UserCog, Wrench } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/auth/AuthProvider";
import { useSubscription } from "@/contexts/billing/subscription";
import { useCheckSubscriptionStatus } from "@/hooks/billing/useCheckSubscriptionStatus";
import { ImpersonationService } from "@/services/users/ImpersonationService";
import { ActivePhoneNumbersSection } from "./ActivePhoneNumbersSection";
import { CallPurchasesSection } from "./CallPurchasesSection";
import { DevelopersSection } from "./DevelopersSection";
import { PasswordSection } from "./PasswordSection";
import { PrivacyPoliciesSection } from "./PrivacyPoliciesSection";
import { ProfileSection } from "./ProfileSection";
import { SubscriptionSection } from "./SubscriptionSection";
import { TeamManagementSection } from "./TeamManagementSection";
import { TeamMembersSection } from "./TeamMembersSection";
import { TroubleshootingSection } from "./TroubleshootingSection";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const AccountSettingsPage = () => {
  const { userProfile } = useAuth();
  const { subscription } = useSubscription();
  const { checkSubscriptionStatus } = useCheckSubscriptionStatus();
  const [searchParams] = useSearchParams();
  const isImpersonating = ImpersonationService.isImpersonating();
  const VALID_TABS = ["profile", "security", "billing", "developers", "troubleshooting"];
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "call-purchases") return "billing";
    if (tabParam && VALID_TABS.includes(tabParam)) return tabParam;
    return "profile";
  });

  const handleTeamUpdate = () => {
    // This can be used to trigger any necessary updates after team operations
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("from") === "billing" && subscription?.stripe_id) {
      console.log("User returned from Stripe billing portal to account settings");
      checkSubscriptionStatus(subscription.stripe_id);
    }
  }, [subscription, checkSubscriptionStatus]);

  return (
    <div className="bg-genie-background min-h-screen overflow-hidden">
      <div className="container mx-auto px-4 py-6 max-w-[1920px] h-screen overflow-auto">
        <div className="w-full flex flex-col h-full">
          <div className="flex items-center mb-6">
            <GenieIcon
              name="settings"
              size={28}
              label="Account Settings"
              className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4"
            />
            <h1 className="text-3xl font-bold text-[#222B45]">Account Settings</h1>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <TabsList className="w-fit">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Security
              </TabsTrigger>
              <TabsTrigger value="billing" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Billing
              </TabsTrigger>
              <TabsTrigger value="developers" className="flex items-center gap-2">
                <Code className="w-4 h-4" />
                Developers
              </TabsTrigger>
              <TabsTrigger value="troubleshooting" className="flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Troubleshooting
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto mt-6">
              <TabsContent value="profile" className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <ProfileSection />
                </div>
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <TeamManagementSection onTeamUpdate={handleTeamUpdate} />
                  <TeamMembersSection onTeamUpdate={handleTeamUpdate} />
                  <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)] flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                        <KeyRound className="h-5 w-5 text-genie-primary" />
                      </div>
                      <h2 className="text-xl font-semibold m-0">Need to change password?</h2>
                    </div>
                    <button
                      onClick={() => setActiveTab("security")}
                      className="bg-transparent border border-[#4E9CFF] px-2 py-1 rounded text-xs cursor-pointer text-genie-primary"
                    >
                      Edit
                    </button>
                  </div>
                  {isImpersonating && (
                    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)] flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                          <UserCog className="h-5 w-5 text-genie-primary" />
                        </div>
                        <h2 className="text-xl font-semibold m-0">Edit profile as admin</h2>
                      </div>
                      <button
                        onClick={() => {
                          const params = new URLSearchParams({ tab: "users" });
                          if (userProfile?.email) {
                            params.set("edit", userProfile.email);
                          }
                          ImpersonationService.stopImpersonation(`/admin/users?${params.toString()}`);
                        }}
                        className="bg-transparent border border-[#4E9CFF] px-2 py-1 rounded text-xs cursor-pointer text-genie-primary"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="security" className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <PasswordSection />
                </div>
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <PrivacyPoliciesSection />
                </div>
              </TabsContent>

              <TabsContent value="billing" className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <SubscriptionSection />
                  <CallPurchasesSection />
                </div>
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <ActivePhoneNumbersSection />
                </div>
              </TabsContent>

              <TabsContent value="developers" className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <DevelopersSection />
                </div>
              </TabsContent>

              <TabsContent value="troubleshooting" className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] flex flex-col gap-5 overflow-auto">
                  <TroubleshootingSection />
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
